import React, {useState, useEffect, useCallback} from 'react';
import PropTypes from "prop-types";
import {useSelector, useDispatch} from "react-redux";
import {useNavigate} from 'react-router-dom';
import TableViewHook from "../../containers/TableViewHook";
import TableGridHook from "../../containers/TableGridHook";
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Fab from '@mui/material/Fab';
import AddIcon from "@mui/icons-material/Add";
import {setQuestionnaireVersionsModeFlag, questionnaireTemplateInitClear} from "../../actions/questionnaireTemplateActions";
import {openDrawer} from "../../actions/drawerActions";
import {QD_URL} from "../../constants/systemConstants";
import FilterPopOverHook from "../dialogs/filters/FilterPopOverHook";
import {QUESTIONNAIRE_COMPLEX} from "../../constants/questionnaireType";
import {setQuestionnaireType} from "../../actions/questionnaireEditorActions";
import ReviewDrawer, {reviewDrawerProps} from "../drawers/ReviewDrawerHook";
import {getTemplateList, updatePageAndGetList, updateSortAndGetList} from "../../actions/templateListActions";
import formatCopyText from "../../utilities/formatCopyText";
import ArgoListTitleAndLoadingAnimationHook from "../common/ArgoListTitleAndLoadingAnimationHook";
import * as TEMPLATE from "../../constants/templateConstants";
import * as SYSTEM from "../../constants/systemConstants";
import ArgoScreenSizePopUpHook from "../common/ArgoScreenSizePopUpHook"

const QuestionnaireList = ({ match, location }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState(false);
    const [closePopUp, setClosePopUp] = useState(false);

    const questionnaireTemplateList = useSelector(state => state.questionnaireTemplateList);
    const questionnaireInit = useSelector(state => state.questionnaireInit);
    const filterState = useSelector(state => state.filter[TEMPLATE.FILTER.current.questionnaire]);

    useEffect(() => {
        setLoading(true);
        dispatch(setQuestionnaireVersionsModeFlag(false)); // Just in case version list was closed and returned here
        dispatch(getTemplateList(filterState, TEMPLATE.TYPE.QUESTIONNAIRE, TEMPLATE.MODE.CURRENT));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        if (filter) {
            setFilter(false);
        }
        else if (questionnaireInit.init) {
            setLoading(true);
            setFilter(true);
            dispatch(questionnaireTemplateInitClear());
        }
        // else if ((!loading) && (questionnaireInit.clone)) {
        //     setLoading(true);
        //     setFilter(true);
        //     dispatch(questionnaireTemplateCloneClear());
        // }
        else if ((loading) && (questionnaireInit.clone)) {
            if (SYSTEM.USE_TIMER_DELAY) {
                // NOTE: Only use for local testing of loading render animation, before using for PRODUCTION set SYSTEM.USE_TIMER_DELAY_AMOUNT = false in systemConstants.js
                setTimeout(delayedSetLoadingFalse, SYSTEM.USE_TIMER_DELAY_AMOUNT); // 1000ms = 1 second, // Used to test loading animation rendering
            }
            else {
                setLoading(false);
            }
        }
        else if ((loading) && (!questionnaireInit.init)) {
            if (SYSTEM.USE_TIMER_DELAY) {
                // NOTE: Only use for local testing of loading render animation, before using for PRODUCTION set SYSTEM.USE_TIMER_DELAY_AMOUNT = false in systemConstants.js
                setTimeout(delayedSetLoadingFalse, SYSTEM.USE_TIMER_DELAY_AMOUNT); // 1000ms = 1 second, // Used to test loading animation rendering
            }
            else {
                setLoading(false);
            }
        }
        else if ((loading) && (!questionnaireInit.clone)) {
            if (SYSTEM.USE_TIMER_DELAY) {
                // NOTE: Only use for local testing of loading render animation, before using for PRODUCTION set SYSTEM.USE_TIMER_DELAY_AMOUNT = false in systemConstants.js
                setTimeout(delayedSetLoadingFalse, SYSTEM.USE_TIMER_DELAY_AMOUNT); // 1000ms = 1 second, // Used to test loading animation rendering
            }
            else {
                setLoading(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questionnaireInit, questionnaireTemplateList, filterState, filter, dispatch]);

    // Used to test loading animation rendering
    const delayedSetLoadingFalse = () => {
        setLoading(false);
    };

    const navigateToEdit = useCallback((templateKey, templateId) => {

        let path = QD_URL + "/questionnaire-editor/" + templateKey + "/" + templateId + "/0"; // CloneMode 0=No, 1=Yes
        navigate(path, {replace: true});
    }, [navigate]);

    const navigateToClone = useCallback((templateKey, templateId, templateMode, type) => {
        let path = QD_URL + "/questionnaire-editor/" + templateKey + "/" + templateId + "/1"; // CloneMode 0=No, 1=Yes
        navigate(path, {replace: true});
        dispatch(setQuestionnaireType(type));
    }, [navigate, dispatch]);

    const navigateToCreate = useCallback((type) => {

        navigate(QD_URL + "/questionnaire-editor/", {replace: true});
        dispatch(setQuestionnaireType(type));
    }, [navigate, dispatch]);

    const openQuestionnaireReviewDrawer = useCallback((templateKey, id, name, row, event) => {
        formatCopyText(row, event, "Questionnaire:"); // If user uses special keys when clicking title format the clipboard CTRL, ALT or CTRL + ALT
        dispatch(openDrawer(reviewDrawerProps, <ReviewDrawer templateId={parseInt(id)} templateKey={templateKey} templateType={TEMPLATE.TYPE.QUESTIONNAIRE}/>));
    }, [dispatch]);

    const onPageChange = useCallback((page, size, event) => {
        setLoading(true);
        setFilter(true);
        dispatch(updatePageAndGetList(page, size, filterState, TEMPLATE.TYPE.QUESTIONNAIRE, TEMPLATE.MODE.CURRENT, event));
    }, [dispatch, filterState]);

    const onSortChange = useCallback((sort, direction) => {
        setLoading(true);
        setFilter(true);
        dispatch(updateSortAndGetList(sort, direction, filterState, TEMPLATE.TYPE.QUESTIONNAIRE, TEMPLATE.MODE.CURRENT));
    }, [dispatch, filterState]);

    const setLoadingStateTrue = useCallback((callback = function () {}, parm1 = null, parm2 = null) => {
        //NOTE: This had to be different from the other list screens
        setLoading(true);
        if ((parm1 !== null)&&(parm2 !== null)) {
            setFilter(true);
            callback(parm1, parm2); // This must be from the change date drawer, so set filter flag also so render animation will work
        }
        else if (parm1 !== null) {
            setFilter(false);
            callback(parm1);
        } else {
            setFilter(false);
            callback();
        }
    }, []);

    const setSavePortalReferenceJsonTrue = useCallback(() => {
        setLoading(true);
        dispatch(getTemplateList(filterState, TEMPLATE.TYPE.QUESTIONNAIRE, TEMPLATE.MODE.CURRENT));
    }, [dispatch, filterState]);

    const handleClosePopUp = () => {
        localStorage.setItem("hasSeenWelcomeMessage", "true");
        setClosePopUp(true);
    };

    // const setFloatingActionButtonCreateOptions = () => {
    //     // NOTE: Plan is to drop support for simple and dynamic questionnaires
    //     return (
    //         <div>
    //             <Fab id="QuestionnaireList-fab"
    //                  onClick={() => navigateToCreate(QUESTIONNAIRE_COMPLEX)}
    //                  color="primary">
    //                 <AddIcon color="white"/>
    //             </Fab>
    //         </div>
    //     );
        // return this.props.questionnaireTypeDisplayed === 'full' ?
        //     <Fab id="QuestionnaireList-menu"
        //          onClick={this.navigateToCreate.bind(this, QUESTIONNAIRE_COMPLEX))}
        //          iconButtonElement={
        //              <IconButton id="QuestionnaireList-menu-icon-button"
        //                          onClick={(event) => {event.stopPropagation();}}
        //                          style={{height: "56px", width: "56px"}}
        //                          iconStyle={{color: style.palette.alternateTextColor}}>
        //                   <AddIcon/>
        //               </IconButton>}
        //               useLayerForClickAway
        //               anchorOrigin={{horizontal: 'left', vertical: 'top'}}
        //               targetOrigin={{horizontal: 'left', vertical: 'top'}}
        //               style={{borderRadius: '50%', backgroundColor: style.palette.accent1Color, height: "56px", width: "56px", border: "10px", textAlign: "center"}}>
        //         <div>
        //             <MenuItem primaryText="Create simple questionnaire" onClick={this.navigateToCreate.bind(this, QUESTIONNAIRE_SIMPLE)}></MenuItem>
        //             {/*<MenuItem primaryText="Create dynamic questionnaire" onClick={this.navigateToCreate.bind(this, QUESTIONNAIRE_DYNAMIC)}></MenuItem>*/}
        //             <MenuItem primaryText="Create complex questionnaire" onClick={this.navigateToCreate.bind(this, QUESTIONNAIRE_COMPLEX)}></MenuItem>
        //         </div>
        //     </Fab>
        //     :
        //     <div>
        //         <Fab id="QuestionnaireList-fab"
        //              onClick={this.navigateToCreate.bind(this, this.props.questionnaireTypeDisplayed === 'simple-only' ? QUESTIONNAIRE_SIMPLE : QUESTIONNAIRE_COMPLEX)}>
        //             <AddIcon />
        //         </Fab>
        //     </div>
    // };

    //let value = this.context;

    let customActions = [
        {
            func: openQuestionnaireReviewDrawer,
            label: 'Review'
        },
        {
            func: navigateToEdit,
            label: "Edit"
        },
        {
            func: navigateToClone,
            label: "Clone"
        }
    ];

    // Used by the versions list and stored in the version filters
    let defaultActions = {
        edit: navigateToEdit,
        create: navigateToCreate,
        review: openQuestionnaireReviewDrawer
    };

    return (
        <div id="QuestionnaireList-container-div" style={{height: "100%", padding: "15px", marginTop: '64px'}}>
            {closePopUp ? "" : <ArgoScreenSizePopUpHook closeFunction={handleClosePopUp}/>}

            <ArgoListTitleAndLoadingAnimationHook fileName="QuestionnaireList" listTitle="Questionnaires" loading={loading} filter={filter}/>

            <div style={{clear: "both"}}></div>

            <Card id="QuestionnaireList-card"
                  variant="outlined"
                  sx={{border: "1px solid lightgray", height: "100%", boxShadow: "2px 4px whitesmoke"
                  }}>
                <CardContent id="QuestionnaireList-card-content">

                    <TableViewHook
                        id="QuestionnaireList-table-view"
                        templateType={TEMPLATE.TYPE.QUESTIONNAIRE}
                        templateMode={TEMPLATE.MODE.CURRENT}
                        setLoadingState={setLoadingStateTrue}
                        filter={<FilterPopOverHook page="questionnaires"
                                               templateType={TEMPLATE.TYPE.QUESTIONNAIRE}
                                               templateMode={TEMPLATE.MODE.CURRENT}
                                               filterName={TEMPLATE.FILTER.current.questionnaire}
                                               submitValue={getTemplateList}
                                               statuses={["All", "Draft", "Published", "Retired"]}
                                               setLoadingState={setLoadingStateTrue}
                                               showOutstandingQuestionnaires={true}
                                               outstanding parentName={"QuestionnaireList"}
                        />}
                    >

                        <TableGridHook
                            gridListType={TEMPLATE.GRID_LIST_TYPE.QUESTIONNAIRE_LIST}
                            id="QuestionnaireList-table-grid"
                            displayType={"questionnaire"}
                            dataSource={questionnaireTemplateList}
                            nameDrawer={openQuestionnaireReviewDrawer}
                            link={QD_URL +"/questionnaire-editor/"}
                            customActions={customActions}
                            pageState = {filterState}
                            onPageChange = {onPageChange}
                            sortState = {filterState}
                            onSortChange = {onSortChange}
                            templateType={TEMPLATE.TYPE.QUESTIONNAIRE}
                            templateMode={TEMPLATE.MODE.CURRENT}
                            showOutstanding={true}
                            showStartDate={true}
                            showEndDate={true}
                            showInUseCount={false}
                            showVersionCount={true}
                            defaultActions={defaultActions}
                            setLoadingState={setLoadingStateTrue}
                            savePortalReferenceJson={setSavePortalReferenceJsonTrue}
                        />
                    </TableViewHook>

                    <div id="QuestionnaireList-fab-div" title={"Create questionnaire"} style={{bottom: "2.5rem", right: "2.5rem", position: "fixed"}}>
                        <Fab id="QuestionnaireList-fab"
                             onClick={() => navigateToCreate(QUESTIONNAIRE_COMPLEX)}
                             color="primary"
                             sx={{float: "right"}}>
                            <AddIcon color="white"/>
                        </Fab>
                    </div>

                </CardContent>

            </Card>

            {/*<div id="QuestionnaireList-fab" title={"Create questionnaire"} style={{bottom: "2.5rem", right: "2.5rem", position: "fixed"}}>*/}
            {/*    <Fab id="QuestionnaireList-fab"*/}
            {/*         onClick={this.navigateToCreate.bind(this, QUESTIONNAIRE_COMPLEX)}*/}
            {/*         color="primary"*/}
            {/*    sx={{float: "right"}}>*/}
            {/*        <AddIcon color="white"/>*/}
            {/*    </Fab>*/}
            {/*</div>*/}

        </div>
    );
};

QuestionnaireList.propTypes = {
    match: PropTypes.object,
    location: PropTypes.object,
};

export default QuestionnaireList;
